// F-MM-N30 — small pure helpers shared by the house-account dialogs.

/** Manager PINs are exactly 4 digits server-side; keep the field to that. */
export function sanitizeManagerPin(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 4)
}

export function isValidManagerPin(pin: string): boolean {
  return /^\d{4}$/.test(pin)
}

/** A charge-to-account refusal the dialog handles in place, not as a toast. */
export interface ChargeAccountServerError {
  code: string
  message: string
  /** Changes on every refusal so the same code twice still re-applies. */
  at: number
}

export const CHARGE_ACCOUNT_INLINE_CODES: ReadonlySet<string> = new Set([
  'tab_credit_limit_exceeded',
  'manager_pin_required',
  'invalid_manager_pin',
  'house_account_kind_disabled',
])

/** What the charge dialog does with a server refusal. */
export function chargeErrorEffect(code: string): { forcePin: boolean; clearPin: boolean; wrongPin: boolean } {
  switch (code) {
    case 'tab_credit_limit_exceeded':
    case 'manager_pin_required':
      return { forcePin: true, clearPin: false, wrongPin: false }
    case 'invalid_manager_pin':
      return { forcePin: true, clearPin: true, wrongPin: true }
    default:
      return { forcePin: false, clearPin: false, wrongPin: false }
  }
}

/** Orders that are never filed, so they get no PRA tax invoice prompt. */
export function isNeverFiledTreatment(taxTreatment: string | null | undefined): boolean {
  return taxTreatment === 'staff_meal' || taxTreatment === 'marketplace_settled'
}

/** Status label for an account the counter can only take money from. */
export function settleOnlyLabel(status: string): string | null {
  if (status === 'suspended') return 'Suspended — payment only'
  if (status === 'closed') return 'Closed — payment only'
  return null
}

/** Parses a required, non-negative money field. Empty is refused, never 0. */
export function parseAmountField(raw: string): { value: number | null; error: string | null } {
  const t = raw.trim()
  if (t === '') return { value: null, error: 'Enter an amount (0 for none).' }
  const n = Number(t)
  if (!Number.isFinite(n) || n < 0) return { value: null, error: 'Enter a number of 0 or more.' }
  return { value: n, error: null }
}

/**
 * Payment label for an order list. A house-account bill stores the account's
 * rate intent ("card" / "cash") in checkout_payment_method, which is not how
 * it was paid — name the account instead.
 */
export function orderPaymentLabel(row: {
  tax_treatment?: string | null
  charged_to_account?: boolean
  checkout_payment_method?: string | null
}): string {
  if (row.tax_treatment === 'staff_meal') return 'Staff meal'
  if (row.charged_to_account) return 'Tab'
  const m = row.checkout_payment_method?.trim()
  return m ? m.charAt(0).toUpperCase() + m.slice(1) : '—'
}
